import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ANNOUNCEMENT_FONTS as F,
  ANNOUNCEMENT_PALETTE as P,
  announcementContainerStyle,
  type AnnouncementStyleName,
} from './announcement-style';
import { ANNOUNCEMENT_STYLE_CARDS } from './styles';

export type AnnouncementPreviewViewport = 'phone' | 'desktop';

export interface AnnouncementPreviewProps {
  style: AnnouncementStyleName;
  title: string;
  body?: string | null;
  mediaType?: 'none' | 'image' | 'video' | 'animation';
  mediaUrl?: string | null;
  posterUrl?: string | null;
  /** Button text; falls back to the student app's "Got it". */
  primaryLabel?: string | null;
  skippable: boolean;
  /** BCP-47 locale for the date ("pt-BR" / "en-US"). */
  locale: string;
  viewport?: AnnouncementPreviewViewport;
  date?: Date;
  /** Shown in place of a native animation, which only the student app can play. */
  animationPlaceholder?: string;
  genderContext?: 'male';
}

/** Frame widths in CSS px. Desktop is drawn large and zoomed down to fit a side column. */
const PHONE_WIDTH = 360;
const DESKTOP_WIDTH = 760;
const DESKTOP_ZOOM = 0.5;

/**
 * Non-interactive rendering of an announcement exactly as the student app
 * draws it (same card components, same overlay), inside a phone- or
 * desktop-sized frame. Used by the admin form so the founder sees the pop-up
 * take shape while typing.
 */
export function AnnouncementPreview(props: AnnouncementPreviewProps) {
  const { t } = useTranslation('p2e');
  const primaryRef = useRef<HTMLButtonElement>(null);
  const viewport = props.viewport ?? 'phone';
  const { Card, sheetOnMobile } = ANNOUNCEMENT_STYLE_CARDS[props.style];
  const pinToBottom = viewport === 'phone' && !!sheetOnMobile;

  const media =
    props.mediaType === 'image' && props.mediaUrl ? (
      <img
        src={props.mediaUrl}
        alt=""
        style={{ display: 'block', width: '100%', maxHeight: 260, objectFit: 'contain', borderRadius: 2 }}
      />
    ) : props.mediaType === 'video' ? (
      <div
        style={{
          width: '100%',
          aspectRatio: '16 / 9',
          background: props.posterUrl ? `#000 url(${props.posterUrl}) center / contain no-repeat` : '#000',
          borderRadius: 2,
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: 'rgba(255,248,245,.85)',
            display: 'grid',
            placeItems: 'center',
            color: P.maxRed,
            fontSize: 18,
          }}
        >
          ▶
        </span>
      </div>
    ) : props.mediaType === 'animation' ? (
      <div
        style={{
          width: '100%',
          aspectRatio: '16 / 9',
          background: P.ink,
          color: P.mutedOnDark,
          borderRadius: 2,
          display: 'grid',
          placeItems: 'center',
          fontFamily: F.cond,
          fontSize: 11,
          letterSpacing: '.2em',
          textTransform: 'uppercase',
          textAlign: 'center',
          padding: 12,
        }}
      >
        {props.animationPlaceholder ?? 'Animation'}
      </div>
    ) : null;

  return (
    <div
      aria-hidden="true"
      data-testid="announcement-preview"
      data-viewport={viewport}
      style={{
        ...announcementContainerStyle,
        width: viewport === 'phone' ? PHONE_WIDTH : DESKTOP_WIDTH,
        maxWidth: '100%',
        minHeight: viewport === 'phone' ? 560 : 520,
        zoom: viewport === 'phone' ? 1 : DESKTOP_ZOOM,
        background: 'rgba(26,16,24,0.9)',
        backgroundImage: 'radial-gradient(rgba(255,248,245,.06) 1px, transparent 1.4px)',
        backgroundSize: '14px 14px',
        display: 'flex',
        alignItems: pinToBottom ? 'flex-end' : 'center',
        justifyContent: 'center',
        padding: pinToBottom ? 0 : 16,
        borderRadius: 12,
        overflow: 'hidden',
        pointerEvents: 'none',
        userSelect: 'none',
      }}
    >
      <Card
        title={props.title}
        body={props.body ?? null}
        media={media}
        date={props.date ?? new Date()}
        locale={props.locale}
        primaryLabel={props.primaryLabel || t('announcements.understood')}
        primaryDisabled={false}
        primaryRef={primaryRef}
        onPrimary={() => undefined}
        skippable={props.skippable}
        skipLabel={t('announcements.skip')}
        onSkip={() => undefined}
        gatedHint={null}
        genderContext={props.genderContext}
        reducedMotion
      />
    </div>
  );
}
